/**
 * What dragging one step of a drip sequence MEANS.
 *
 * A drip is not a list of dates. It is a start time plus, on each step, the gap
 * that follows it (`DripStepGap`), and every step's send time is the forward
 * walk of those gaps (`projectFollowupSendDates`). So a step cannot simply be
 * "moved" to a date the way a message can — the sequence has to be re-expressed
 * so the walk lands where the user dropped:
 *
 *   - the FIRST step is the sequence's start, so moving it moves the whole
 *     sequence (later steps keep their gaps and follow along);
 *   - a LATER step is placed by the gap after the step before it, so moving it
 *     changes THAT gap. The steps after it keep their own gaps, so they shift
 *     by the same amount — the natural reading of "give people another day
 *     before message 3".
 *
 * Writing the moved step's own date instead is what produced the doubled
 * sequence: the drip kept projecting the original run from its unchanged start
 * while the moved campaign projected a second one.
 *
 * Pure: the caller dispatches.
 */
import type { RecurrenceUnit } from '@bt-common/schemas/bt/campaign';

export interface DripStepGap { value: number; unit: RecurrenceUnit }
export interface DripStepLike { stepIndex: number; gapAfter?: DripStepGap | null }

export type DripDropRefusal =
    /** Dropped before the step it follows — the sequence would run backwards. */
    | 'before-previous-step'
    | 'unknown-step';

export type DripDropResolution =
    | { kind: 'noop' }
    | { kind: 'refused'; reason: DripDropRefusal }
    /**
     * Move the whole sequence: patch the drip's start (and its step-0 campaign).
     *
     * `shiftMs` is how far the start moved. A REPEATING sequence needs it as well
     * as the new date: its runs come from a rule, so the rule's anchor has to move
     * by the same delta or the rule keeps emitting the old dates while the start
     * claims otherwise.
     */
    | { kind: 'move-sequence'; dueDate: string; shiftMs: number }
    /** Re-time one step by changing the gap that places it. */
    | { kind: 'set-gap'; stepIndex: number; gap: DripStepGap; dueDate: string };

const MIN = 60_000, HOUR = 60 * MIN, DAY = 24 * HOUR, WEEK = 7 * DAY;

/**
 * Express a duration as a gap, preferring the largest unit that divides it
 * exactly — a two-day move stays "2 days" in the sequence editor rather than
 * becoming "2880 minutes". Minutes are the floor, and every drop lands on the
 * calendar's own minute grid, so the gap is always exact.
 */
export function msToGap(ms: number): DripStepGap {
    const whole = Math.max(0, Math.round(ms / MIN) * MIN);
    if (whole % WEEK === 0 && whole >= WEEK) return { value: whole / WEEK, unit: 'week' };
    if (whole % DAY === 0 && whole >= DAY) return { value: whole / DAY, unit: 'day' };
    if (whole % HOUR === 0 && whole >= HOUR) return { value: whole / HOUR, unit: 'hour' };
    return { value: whole / MIN, unit: 'minute' };
}

/** The gap durations, in ms. Mirrors `gapToMs` in campaign-schedule. */
export function gapMs(gap?: DripStepGap | null): number {
    if (!gap || typeof gap.value !== 'number' || gap.value <= 0) return 0;
    switch (gap.unit) {
        case 'minute': return gap.value * MIN;
        case 'hour': return gap.value * HOUR;
        case 'day': return gap.value * DAY;
        case 'week': return gap.value * WEEK;
        case 'month': return Math.round(gap.value * 30.4375 * DAY);
        case 'year': return Math.round(gap.value * 365.25 * DAY);
        default: return 0;
    }
}

/** Where each step lands, walking the gaps forward from the sequence start. */
export function stepSendTimes(startMs: number, steps: ReadonlyArray<DripStepLike>): number[] {
    const out: number[] = [];
    let running = startMs;
    for (const step of steps) {
        out.push(running);
        running += gapMs(step.gapAfter);
    }
    return out;
}

export interface DripDropInput {
    /** The sequence's first send — the materialized step-0 campaign's due date,
     *  falling back to the drip's own schedule. */
    startMs: number;
    steps: ReadonlyArray<DripStepLike>;
    /** Which step was dragged. */
    stepIndex: number;
    /** Where it was dropped. */
    targetMs: number;
    isRecurringDrip?: boolean;
}

export function resolveDripStepDrop(input: DripDropInput): DripDropResolution {
    const { startMs, steps, stepIndex, targetMs, isRecurringDrip } = input;

    if (!Array.isArray(steps) || stepIndex < 0 || stepIndex >= steps.length) {
        return { kind: 'refused', reason: 'unknown-step' };
    }

    const times = stepSendTimes(startMs, steps);
    if (times[stepIndex] === targetMs) return { kind: 'noop' };

    if (stepIndex === 0) {
        // Moving the first step moves the sequence's START — including for a
        // REPEATING sequence, where every run shifts by the same delta.
        //
        // This used to be refused, on the grounds that one run of a repeating
        // sequence cannot be re-dated on its own. That was inconsistent: the same
        // objection applies to every LATER step, and there it is accepted — changing
        // the gap between step 1 and step 2 already applies to EVERY run, because a
        // gap belongs to the sequence, not to one run. The model is therefore already
        // "edit the sequence, seen through one run", and under it dragging step 0 is
        // simply editing the sequence's start. Holding the first step to a stricter
        // standard than its siblings had no principled basis and surprised the user,
        // who reasonably expects the first message to move like the others.
        void isRecurringDrip;
        return {
            kind: 'move-sequence',
            dueDate: new Date(targetMs).toISOString(),
            shiftMs: targetMs - times[0],
        };
    }

    const previousAt = times[stepIndex - 1];
    if (targetMs < previousAt) {
        return { kind: 'refused', reason: 'before-previous-step' };
    }

    return {
        kind: 'set-gap',
        stepIndex: stepIndex - 1,
        gap: msToGap(targetMs - previousAt),
        dueDate: new Date(targetMs).toISOString(),
    };
}

/** `YYYYMMDDTHHMMSSZ` — the compact UTC stamp both DTSTART and UNTIL use. */
const RRULE_STAMP = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/;

const parseStamp = (stamp: string): Date | null => {
    const m = RRULE_STAMP.exec(stamp);
    if (!m) return null;
    const [, y, mo, d, h, mi, s] = m;
    return new Date(Date.UTC(+y, +mo - 1, +d, +h, +mi, +s));
};

const formatStamp = (date: Date): string =>
    date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}Z$/, 'Z');

/** RRULE weekday tokens, in the order RFC 5545 numbers them from Monday. */
const RRULE_WEEKDAYS = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'];

/** Monday-based index of a date, matching RRULE's own weekday numbering. */
const weekdayIndex = (date: Date): number => (date.getUTCDay() + 6) % 7;

/**
 * Move a rule's BYDAY / BYMONTHDAY so the pattern lands where the anchor now does.
 *
 * BYDAY shifts by the same number of weekdays the anchor moved, wrapping round the
 * week, and keeps any ordinal prefix ("+2MO" becomes "+2WE") so the "second such
 * weekday of the month" idea survives the move. BYMONTHDAY simply becomes the new
 * anchor's day of the month.
 *
 * A shift of a whole number of weeks leaves BYDAY untouched, which falls out of the
 * arithmetic rather than being special-cased.
 */
const shiftDayConstraints = (rruleset: string, before: Date, after: Date): string => {
    const dayDelta = (weekdayIndex(after) - weekdayIndex(before) + 7) % 7;

    let out = rruleset;

    if (dayDelta !== 0) {
        out = out.replace(/(BYDAY=)([^;\n]+)/i, (_whole, prefix: string, list: string) => {
            const moved = list.split(',').map(token => {
                const match = /^([+-]?\d+)?([A-Za-z]{2})$/.exec(token.trim());
                if (!match) return token;
                const ordinal = match[1] ?? '';
                const index = RRULE_WEEKDAYS.indexOf(match[2].toUpperCase());
                if (index < 0) return token;
                return `${ordinal}${RRULE_WEEKDAYS[(index + dayDelta) % 7]}`;
            });
            return `${prefix}${moved.join(',')}`;
        });
    }

    return out.replace(/(BYMONTHDAY=)(-?\d+)/i, (_whole, prefix: string) => `${prefix}${after.getUTCDate()}`);
};

/**
 * Move a recurrence rule's ANCHOR by `deltaMs`, so every run it emits shifts with it.
 *
 * Needed when the start of a REPEATING drip is dragged. Patching only the drip's
 * `dueDate` would leave the rule anchored to the old date: it would go on emitting
 * the original run dates while the start claimed to be somewhere else, and the
 * calendar would show a sequence whose runs do not line up with its own beginning.
 * That is the same class of mis-seating as a recurring message left due at an
 * instant its rule no longer emits.
 *
 * UNTIL travels with the anchor. Leaving a fixed end behind while the start moves
 * forward silently costs the sequence runs off the end of its own window.
 * COUNT needs no adjustment — it is a number of runs, not a date.
 *
 * EXDATE travels with the anchor too, for the reason that matters most here: an
 * EXDATE is an instant, and it only excludes anything while it coincides EXACTLY
 * with an instant the rule emits. Move the anchor and leave the exclusions behind
 * and every one of them stops matching — so a run the customer DELETED comes back
 * and goes out. `RRuleRecurrenceHandler` already offsets them on its own
 * whole-series move for this reason; the two must not answer differently, or the
 * same customer act means one thing through the dialog and another through a drag.
 *
 * A rruleset with no readable DTSTART is returned untouched: there is no anchor to
 * move, and inventing one is how a rule ends up with a send time nobody chose.
 */
export function shiftRrulesetAnchor(rruleset: string, deltaMs: number): string {
    if (!rruleset || !deltaMs) return rruleset;

    let anchorBefore: Date | null = null;
    let anchorAfter: Date | null = null;

    const shifted = rruleset.replace(
        /(DTSTART(?:;[^:\n]*)?:)(\d{8}T\d{6}Z)/,
        (whole, prefix: string, stamp: string) => {
            const at = parseStamp(stamp);
            if (!at) return whole;
            anchorBefore = at;
            anchorAfter = new Date(at.getTime() + deltaMs);
            return `${prefix}${formatStamp(anchorAfter)}`;
        },
    );
    if (shifted === rruleset || !anchorBefore || !anchorAfter) return rruleset; // no anchor — leave it alone

    // The rule's DAY CONSTRAINTS describe the same pattern the anchor does, so they
    // travel with it. Without this a `BYDAY=TU` sequence dragged to a Thursday keeps
    // emitting Tuesdays: the anchor moves two days, the runs move to the FOLLOWING
    // Tuesday, and the sequence lands five days from where the user let go of it.
    const withPattern = shiftDayConstraints(shifted, anchorBefore, anchorAfter);

    const withUntil = withPattern.replace(
        /(UNTIL=)(\d{8}T\d{6}Z)/,
        (whole, prefix: string, stamp: string) => {
            const at = parseStamp(stamp);
            return at ? `${prefix}${formatStamp(new Date(at.getTime() + deltaMs))}` : whole;
        },
    );

    return shiftExdates(withUntil, deltaMs);
}

/**
 * Move every EXDATE instant by the same delta as the anchor.
 *
 * An EXDATE line is comma-separated and there can be several of them, so this
 * rewrites each stamp wherever it appears rather than the first one. A stamp
 * that will not parse is left exactly as it was: dropping an exclusion sends a
 * message the customer deleted, which is the failure this whole function exists
 * to avoid, so an unreadable one stays put and keeps whatever it was matching.
 */
const shiftExdates = (rruleset: string, deltaMs: number): string =>
    rruleset.replace(/^(EXDATE(?:;[^:\n]*)?:)(.+)$/gim, (_whole, prefix: string, list: string) => {
        const moved = list.split(',').map(token => {
            const trimmed = token.trim();
            const at = parseStamp(trimmed);
            return at ? formatStamp(new Date(at.getTime() + deltaMs)) : token;
        });
        return `${prefix}${moved.join(',')}`;
    });
